import { CheckIcon } from 'components/Icons/CheckIcon'
import { NewPartnerStep } from 'features/Partners/types'
import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

const StepNumberContainer = styled.div<{ isActive?: boolean; isChecked?: boolean }>`
  background-color: ${({ isActive, isChecked }): string => {
    if (isActive) return '#8F8BFF'
    if (isChecked) return '#00C875'
    return '#EAEAFF'
  }};
  border-radius: 24px;
  width: 36px;
  min-width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledCheckIcon = styled(CheckIcon)`
  width: 16px;
  min-width: 16px;
  fill: #ffffff;
`

const StepNumberValue = styled.div<{ isActive?: boolean }>`
  color: ${({ isActive }): string => (isActive ? '#ffffff' : '#5D59D7')};
  font-size: 13px;
`

const StepNumber = ({
  isActive,
  isChecked,
  stepNb
}: {
  isActive: boolean
  isChecked: boolean
  stepNb: number
}): ReactElement => {
  if (isChecked) {
    return (
      <StepNumberContainer isChecked>
        <StyledCheckIcon />
      </StepNumberContainer>
    )
  }
  return (
    <StepNumberContainer isActive={isActive}>
      <StepNumberValue isActive={isActive}>{stepNb}</StepNumberValue>
    </StepNumberContainer>
  )
}

const Container = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  li + li:before {
    content: ${`"\\203A"`};
    color: #717171;
    font-size: 24px;
  }
`

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
`

const StepLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #423f9a;
`
const AnchorButton = styled.button`
  color: #423f9a;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
`

type ItemLinkProps = {
  isClickeable?: boolean
  children: React.ReactNode
  onClick: () => void
}

const ItemLink = ({ isClickeable, children, onClick }: ItemLinkProps): ReactElement => {
  return isClickeable ? <AnchorButton onClick={onClick}>{children}</AnchorButton> : <span>{children}</span>
}

export const NewPartnerBreadcrumb = ({
  currentStep,
  isEdit,
  setStepNb,
  completedSteps
}: {
  currentStep: NewPartnerStep
  isEdit?: boolean
  setStepNb: (stepNb: number) => void
  completedSteps: NewPartnerStep[]
}): ReactElement => {
  return (
    <Container>
      <Item>
        <StepNumber
          isActive={currentStep === NewPartnerStep.DATA}
          isChecked={completedSteps.includes(NewPartnerStep.DATA)}
          stepNb={NewPartnerStep.DATA}
        />
        <ItemLink
          isClickeable={isEdit && currentStep !== NewPartnerStep.DATA}
          onClick={(): void => setStepNb(NewPartnerStep.DATA)}
        >
          <StepLabel>
            <FormattedMessage id="partners.new.breadcrumb.step1" defaultMessage="Partner Data" />
          </StepLabel>
        </ItemLink>
      </Item>
      <Item>
        <StepNumber
          isActive={currentStep === NewPartnerStep.GENERAL_INFORMATION}
          isChecked={completedSteps.includes(NewPartnerStep.GENERAL_INFORMATION)}
          stepNb={NewPartnerStep.GENERAL_INFORMATION}
        />
        <ItemLink
          isClickeable={isEdit && isEdit && currentStep !== NewPartnerStep.GENERAL_INFORMATION}
          onClick={(): void => setStepNb(NewPartnerStep.GENERAL_INFORMATION)}
        >
          <StepLabel>
            <FormattedMessage id="partners.new.breadcrumb.step2" defaultMessage="General Information" />
          </StepLabel>
        </ItemLink>
      </Item>
      <Item>
        <StepNumber
          isActive={currentStep === NewPartnerStep.DOCS}
          isChecked={completedSteps.includes(NewPartnerStep.DOCS)}
          stepNb={NewPartnerStep.DOCS}
        />
        <ItemLink
          isClickeable={isEdit && isEdit && currentStep !== NewPartnerStep.DOCS}
          onClick={(): void => setStepNb(NewPartnerStep.DOCS)}
        >
          <StepLabel>
            <FormattedMessage id="partners.new.breadcrumb.step3" defaultMessage="Documents" />
          </StepLabel>
        </ItemLink>
      </Item>
    </Container>
  )
}
